import KeenSlider from 'keen-slider'
import { matchesMediaQuery } from '../utilities'
import { ACTIVE_CLASS } from '../utilities/constants'

export default class {
  constructor(el, options) {
    options = Object.assign(
      {
        active: 'true',
        intro: false,
      },
      options,
      {},
    )

    const containerEl = el.querySelector('.js-slideContainer')
    let items = Array.from(containerEl.children)

    const indicatorEls = Array.from(el.querySelectorAll('.js-slideIndicator'))
    const navButtonEls = Array.from(el.querySelectorAll('.js-slideNavButton'))

    const SLIDE_CLASS = 'is-slide'
    let introClone = null
    let introEl = null

    function updateActiveState(sldr) {
      const isActive =
        options.active === 'true' || matchesMediaQuery(options.active)

      if (options.active) {
        el.classList.toggle(ACTIVE_CLASS, isActive)

        if (sldr.options.drag !== isActive) {
          sldr.update({
            ...sldr.options,
            drag: isActive,
          })
        }

        if (!isActive) {
          sldr.moveToIdx(0)
        }
      } else {
        el.classList.add(ACTIVE_CLASS)
      }

      const introActive =
        options.intro &&
        (options.intro === 'true' || matchesMediaQuery(options.intro))

      if (options.intro) {
        if (introActive) {
          el.classList.add('has-intro')

          if (introClone) {
            containerEl.prepend(introClone)
            introClone = null
          }
        } else {
          el.classList.remove('has-intro')

          if (!introClone) {
            introEl = containerEl.querySelector('.js-slideIntro')
            introClone = introEl.parentNode.removeChild(introEl)
            el.classList.remove('has-intro')
          }
        }
      }
    }

    function updateSlider(sldr) {
      let activeSlideIndex = sldr?.track?.details?.rel ?? 0

      // If is clone, set active slide to first slide
      if (sldr?.track?.details?.rel >= sldr.slides.length) {
        activeSlideIndex = activeSlideIndex - sldr.slides.length
      }

      if (indicatorEls?.length) {
        indicatorEls.forEach((indicatorEl, index) => {
          indicatorEl.classList.toggle(ACTIVE_CLASS, activeSlideIndex === index)
        })
      }
    }

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function () {
        items = Array.from(containerEl.children)

        slider.update({
          ...slider.options,
          slides: {
            ...slider.options.slides,
            number: items.length,
          },
        })
      })
    })

    function updateColors(sldr) {
      const leftSlide = sldr.track.details.slides.reduce((acc, slide) => {
        if (slide.distance >= 0 && slide.distance <= 1 - slide.size) {
          if (!acc || slide.distance < acc.distance) {
            return slide
          }
        }
        return acc
      }, 0)
      const leftSlideEl = sldr.slides[sldr.track.absToRel(leftSlide.abs)]
      const rightSlide = sldr.track.details.slides.reduce((acc, slide) => {
        if (slide.distance >= 0 && slide.distance <= 1 - slide.size) {
          if (!acc || slide.distance > acc.distance) {
            return slide
          }
        }
        return acc
      }, 0)

      const rightSlideEl = sldr.slides[sldr.track.absToRel(rightSlide.abs)]

      const leftColor = leftSlideEl?.dataset?.color ?? ''
      const rightColor = rightSlideEl?.dataset?.color ?? ''

      if (leftColor) {
        el.style.setProperty('--slide-color-1', leftColor)
      } else {
        el.style.removeProperty('--slide-color-1')
      }
      if (rightColor) {
        el.style.setProperty('--slide-color-2', rightColor)
      } else {
        el.style.removeProperty('--slide-color-2')
      }
    }

    const slider = new KeenSlider(
      containerEl,
      {
        initial: 0,
        loop: items.length > 1,
        rtl: false,
        slideChanged: updateSlider,
        updated: updateActiveState,
        animationEnded: updateColors,
        created: (sldr) => {
          el.classList.add(SLIDE_CLASS)

          observer.observe(sldr.container, { childList: true })

          updateActiveState(sldr)

          requestAnimationFrame(() => {
            updateSlider(sldr)
            updateColors(sldr)
          })
        },
        rubberband: false,
        mode: 'snap',
        renderMode: 'performance',
        defaultAnimation: {
          duration: 600,
          easing: (time) => 1 - Math.pow(1 - time, 4),
        },
        selector: '.js-slideItem',
        destroyed: () => observer.disconnect(),
        slides: {
          number: items.length,
          perView: 'auto',
        },
      },
      [WheelControls, KeyboardControls],
    )

    if (navButtonEls) {
      navButtonEls.forEach((buttonEl, index) => {
        buttonEl.addEventListener('click', () => {
          if (index === 0) {
            slider.prev()
          } else {
            if (slider.options.loop) {
              slider.next()
            } else {
              if (slider?.track?.details?.rel == items.length - 1) {
                slider.moveToIdx(0)
              } else {
                slider.next()
              }
            }
          }
        })
      })
    }

    if (indicatorEls?.length) {
      indicatorEls.forEach((indicatorEl, index) => {
        indicatorEl.addEventListener('click', () => {
          slider.moveToIdx(index)
        })
      })
    }
  }
}

const KeyboardControls = (slider) => {
  let focused = false

  function eventFocus() {
    focused = true
  }

  function eventBlur() {
    focused = false
  }

  function eventKeydown(e) {
    if (!focused || !slider.options.drag) return

    switch (e.key) {
      default:
        break
      case 'Left':
      case 'ArrowLeft':
        slider.prev()
        break
      case 'Right':
      case 'ArrowRight':
        slider.next()
        break
    }
  }

  slider.on('created', () => {
    slider.container.setAttribute('tabindex', 0)
    slider.container.addEventListener('focus', eventFocus)
    slider.container.addEventListener('blur', eventBlur)
    slider.container.addEventListener('keydown', eventKeydown)
  })
}

const WheelControls = (slider) => {
  let didIt = false

  function eventWheel(e) {
    if (slider.options.drag && !didIt) {
      if (e.deltaX > 40) {
        e.preventDefault()
        slider.next()
        didIt = true
      } else if (e.deltaX < -40) {
        e.preventDefault()
        slider.prev()
        didIt = true
      }
    }
  }

  slider.on('created', () => {
    slider.container.addEventListener('wheel', eventWheel, {
      passive: false,
    })
  })

  slider.on('animationEnded', () => {
    didIt = false
  })
}
